// v10/src/components/admin/FlowTrapConfig.js
// Panel de configuración de ítems trampa — admin/instructor
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Target, Users, User, Plane, AlertTriangle } from 'lucide-react';
import api from '../../services/apiService';
import { aircraftService } from '../../services/apiService';

const FlowTrapConfig = ({ darkMode }) => {
  const [aircraft, setAircraft] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [trapItems, setTrapItems] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formLabel, setFormLabel] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formPilotId, setFormPilotId] = useState('');

  const bg = darkMode ? '#1e293b' : 'white';
  const border = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const text = darkMode ? '#f1f5f9' : '#0f172a';
  const muted = darkMode ? '#64748b' : '#94a3b8';

  // Cargar aeronaves
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await aircraftService.getAll();
        const list = res.data?.data || res.data || [];
        setAircraft(list);
        if (list.length > 0) setSelectedAircraft(list[0]);
      } catch (_) {}

      try {
        const res = await api.get('/api/users', { params: { role: 'pilot' } });
        setPilots(res.data?.data || res.data || []);
      } catch (_) {}

      setLoading(false);
    };
    fetch();
  }, []);

  // Cargar trampas cuando cambia la aeronave
  useEffect(() => {
    if (!selectedAircraft) return;
    const fetchTraps = async () => {
      try {
        const res = await api.get(`/api/flow/trap-items/${selectedAircraft.id}`);
        setTrapItems(res.data?.data || []);
      } catch (_) { setTrapItems([]); }
    };
    fetchTraps();
  }, [selectedAircraft]);

  const handleCreate = async () => {
    if (!formLabel.trim() || !selectedAircraft) return;
    setSaving(true);
    try {
      const res = await api.post('/api/flow/trap-items', {
        aircraft_id:     selectedAircraft.id,
        label:           formLabel.trim(),
        description:     formDesc.trim() || null,
        target_pilot_id: formPilotId || null,
      });
      if (res.data.success) {
        setTrapItems(prev => [res.data.data, ...prev]);
        setFormLabel(''); setFormDesc(''); setFormPilotId('');
        setShowForm(false);
      }
    } catch (_) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/flow/trap-items/${id}`);
      setTrapItems(prev => prev.filter(t => t.id !== id));
    } catch (_) {}
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
          style={{ width: '36px', height: '36px', borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#818cf8' }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <Target style={{ width: '22px', height: '22px', color: '#f59e0b' }} />
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: text }}>Ítems Trampa</h2>
      </div>

      <p style={{ margin: '0 0 20px', fontSize: '13px', color: muted, lineHeight: 1.6 }}>
        Los ítems trampa son verificaciones que no corresponden a la aeronave seleccionada. Se inyectan aleatoriamente durante el Flow Mode para detectar si el piloto está confirmando sin verificar.
      </p>

      {/* ── Selector de aeronave ─────────────────────────────────── */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: muted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Aeronave
        </label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {aircraft.map(ac => (
            <motion.button
              key={ac.id}
              onClick={() => setSelectedAircraft(ac)}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '8px 16px', borderRadius: '12px', border: `1.5px solid ${selectedAircraft?.id === ac.id ? '#818cf8' : border}`,
                background: selectedAircraft?.id === ac.id ? 'rgba(129,140,248,0.1)' : bg,
                color: selectedAircraft?.id === ac.id ? '#818cf8' : text,
                fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <Plane style={{ width: '14px', height: '14px' }} />
              {ac.registration || ac.model_name || ac.name}
            </motion.button>
          ))}
        </div>
      </div>

      {/* ── Lista de trampas ─────────────────────────────────────── */}
      {trapItems.length === 0 && (
        <div style={{ padding: '32px', textAlign: 'center', borderRadius: '16px', background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px dashed ${border}` }}>
          <AlertTriangle style={{ width: '32px', height: '32px', color: muted, margin: '0 auto 8px', opacity: 0.4 }} />
          <p style={{ margin: 0, fontSize: '13px', color: muted }}>No hay ítems trampa configurados para esta aeronave</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        <AnimatePresence>
          {trapItems.map(item => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                padding: '14px 16px', borderRadius: '14px', background: bg,
                border: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: '12px',
              }}
            >
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Target style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: text }}>{item.label}</p>
                {item.description && <p style={{ margin: '2px 0 0', fontSize: '12px', color: muted }}>{item.description}</p>}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  {item.target_pilot_name ? (
                    <span style={{ fontSize: '11px', color: '#818cf8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <User style={{ width: '10px', height: '10px' }} /> {item.target_pilot_name}
                    </span>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Users style={{ width: '10px', height: '10px' }} /> Todos los pilotos
                    </span>
                  )}
                  {item.created_by_name && (
                    <span style={{ fontSize: '11px', color: muted }}>· por {item.created_by_name}</span>
                  )}
                </div>
              </div>
              <motion.button
                onClick={() => handleDelete(item.id)}
                whileTap={{ scale: 0.9 }}
                style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444', flexShrink: 0 }}
              >
                <Trash2 style={{ width: '14px', height: '14px' }} />
              </motion.button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* ── Formulario nuevo ─────────────────────────────────────── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden', marginBottom: '16px' }}
          >
            <div style={{ padding: '20px', borderRadius: '16px', background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${border}` }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: muted, marginBottom: '4px' }}>Nombre del ítem *</label>
                <input
                  value={formLabel}
                  onChange={e => setFormLabel(e.target.value)}
                  placeholder="Ej: Sistema anti-ice"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: `1px solid ${border}`, background: bg, color: text, fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: muted, marginBottom: '4px' }}>Descripción</label>
                <input
                  value={formDesc}
                  onChange={e => setFormDesc(e.target.value)}
                  placeholder="Ej: El C172 básico no tiene anti-ice"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: `1px solid ${border}`, background: bg, color: text, fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: muted, marginBottom: '4px' }}>Piloto específico (opcional)</label>
                <select
                  value={formPilotId}
                  onChange={e => setFormPilotId(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: `1px solid ${border}`, background: bg, color: text, fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                >
                  <option value="">Todos los pilotos</option>
                  {pilots.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <motion.button
                  onClick={handleCreate}
                  disabled={!formLabel.trim() || saving}
                  whileTap={{ scale: 0.95 }}
                  style={{ flex: 1, padding: '10px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg,#818cf8,#6d28d9)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer', opacity: !formLabel.trim() || saving ? 0.5 : 1, fontFamily: 'inherit' }}
                >
                  {saving ? 'Guardando...' : 'Crear trampa'}
                </motion.button>
                <motion.button
                  onClick={() => { setShowForm(false); setFormLabel(''); setFormDesc(''); setFormPilotId(''); }}
                  whileTap={{ scale: 0.95 }}
                  style={{ padding: '10px 20px', borderRadius: '12px', border: `1px solid ${border}`, background: 'transparent', color: muted, fontWeight: 600, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  Cancelar
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!showForm && (
        <motion.button
          onClick={() => setShowForm(true)}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.97 }}
          style={{
            width: '100%', padding: '12px', borderRadius: '14px', border: `1.5px dashed ${border}`,
            background: 'transparent', color: '#818cf8', fontWeight: 700, fontSize: '13px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            fontFamily: 'inherit',
          }}
        >
          <Plus style={{ width: '16px', height: '16px' }} /> Agregar ítem trampa
        </motion.button>
      )}
    </div>
  );
};

export default FlowTrapConfig;
